import { useMemo } from 'react';
import {
  EntityWithDocumentFullResponse,
  PersonCompanyDetails,
  ShareholderDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import {
  ListShareholder,
  RenderIndividualPersonsDetails,
  renderEntityContactDetails,
  renderEntityDetailsSection,
  renderEntityDocuments,
  renderEntityTaxResidence,
} from '@/modules/entity/utils/entity-info/entity-info.tsx';

export const RenderCompanyEntityDetails = ({
  company_entity_details,
  document_files,
  ...entity_details
}: Partial<EntityWithDocumentFullResponse>) => {
  const companyDetails = company_entity_details as
    | (typeof company_entity_details & Partial<PersonCompanyDetails>)
    | undefined;
  const {
    country,
    abn,
    acn,
    nzbn,
    registered_office,
    principal_place_of_business,
    company_name,
    shareholders,
    directors,
    account_holders_gin,
    sponsoring_entity_name,
    is_us_person,
    non_us_person_certification,
    tax_residence,
    entity_number,
    is_managed_by_another_institution,
    crs_status,
    exchange_listed_on,
    ticker,
    public_company_type,
    arbn,
    registered_in_aus_office_address,
    principal_place_in_aus_address,
  } = companyDetails || {};

  const {
    bank_name,
    bank_account_bsb,
    bank_account_swift,
    bank_account_iban,
    bank_account_routing_number,
    bank_account_number,
    bank_account_name,
  } = entity_details || {};

  const subShareholders = useMemo<ListShareholder[]>(() => {
    const directors2 = (company_entity_details?.directors || [])?.map(
      (subShareholder: ShareholderDetails, indexSubShareholder): ListShareholder => ({
        shareholder: subShareholder,
        indexShareholder: 0,
        indexSubShareholder,
        person_details_key: 'directors',
        navName: `Director`,
      }),
    );
    const shareholders2 = (company_entity_details?.shareholders || [])?.map(
      (subShareholder: ShareholderDetails, indexSubShareholder): ListShareholder => ({
        shareholder: subShareholder,
        indexShareholder: 0,
        indexSubShareholder,
        person_details_key: 'shareholders',
        navName: `Shareholder`,
      }),
    );

    return [...directors2, ...shareholders2];
  }, [company_entity_details?.shareholders, company_entity_details?.directors]);

  return (
    <>
      {renderEntityContactDetails(entity_details)}
      {renderEntityDetailsSection({
        items: [
          { label: 'Country', value: country },
          { label: 'NZBN', value: nzbn },
          { label: 'ABN', value: abn },
          { label: 'ACN', value: acn },
          { label: 'Registered office', value: registered_office },
          { label: 'Principal place of business', value: principal_place_of_business },
          { label: 'Company name', value: company_name },
          { label: 'Entity number', value: entity_number },
          { label: 'Exchange listed on', value: exchange_listed_on },
          { label: 'Ticker', value: ticker },
          { label: 'Public company type', value: public_company_type },
          { label: 'ARBN', value: arbn },
          {
            label: 'Registered in Australia office address',
            value: registered_in_aus_office_address,
          },
          { label: 'Principal place in Australia address', value: principal_place_in_aus_address },
        ],
        name: 'Company details',
      })}

      {RenderIndividualPersonsDetails({
        persons: shareholders,
        role: 'Shareholder',
        listStakeholders: subShareholders,
      })}
      {RenderIndividualPersonsDetails({
        persons: directors,
        role: 'Director',
        listStakeholders: subShareholders,
      })}
      {renderEntityTaxResidence(tax_residence)}
      {renderEntityDetailsSection({
        items: [
          { label: 'GIIN', value: account_holders_gin },
          { label: 'Sponsoring entity name', value: sponsoring_entity_name },
          {
            label:
              'Is the Account Holder a specified U.S. person? A U.S. person includes a U.S. citizen or resident alien of the U.S. even if residing outside the U.S.',
            value: is_us_person,
          },
          { label: 'Non U.S Person Certification', value: non_us_person_certification },

          {
            label: 'Is the entity an Investment Entity managed by another Financial Institution?',
            value: is_managed_by_another_institution,
          },
          { label: 'CRS status', value: crs_status },
        ],
      })}
      {renderEntityDetailsSection({
        name: 'Payment instructions',
        items: [
          { label: 'Bank name', value: bank_name },
          { label: 'BSB', value: bank_account_bsb },
          { label: 'SWIFT/BIC', value: bank_account_swift },
          { label: 'IBAN', value: bank_account_iban },
          { label: 'Routing number', value: bank_account_routing_number },
          { label: 'Account number', value: bank_account_number },
          { label: 'Account name', value: bank_account_name },
        ],
      })}
      {renderEntityDocuments(document_files || [])}
    </>
  );
};
